import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import AuthContext from "../../Context/AuthContext";

const CORE_STEPS = [
  { icon: "fa-calendar-days", title: "1. Open a Financial Year", body: "Every entry needs an open accounting period.", link: "/accounting/financial-year" },
  { icon: "fa-sitemap", title: "2. Set up your Chart of Accounts", body: "Seed it for your industry, then adjust as needed.", link: "/accounting/accounts" },
  { icon: "fa-gear", title: "3. Map system accounts", body: "Tell Accountify which accounts are AR, AP, cash, VAT, etc. — required before invoices/vouchers post correctly.", link: "/accounting/config" },
  { icon: "fa-address-book", title: "4. Add your Contacts", body: "Customers and suppliers you'll invoice or pay.", link: "/accounting/contacts" },
];

const CORE_DAILY = [
  { icon: "fa-file-invoice", title: "Invoices", body: "Sales and purchase invoices, with items, VAT and discounts.", link: "/accounting/invoices" },
  { icon: "fa-receipt", title: "Vouchers", body: "Payment, receipt and journal vouchers for anything that isn't an invoice.", link: "/accounting/vouchers" },
  { icon: "fa-table-list", title: "General Ledger", body: "Every posted transaction, by account.", link: "/accounting/ledger" },
  { icon: "fa-landmark", title: "Bank Reconciliation", body: "Match your bank statement against what's recorded.", link: "/accounting/bank/reconcile" },
  { icon: "fa-chart-pie", title: "Reports", body: "Trial Balance, P&L, Balance Sheet, Cash Flow.", link: "/accounting/reports/trial-balance" },
];

const INDUSTRY_CONTENT = {
  school: {
    label: "School",
    intro: "Fees are billed by class group and stream, and student payments carry a running balance forward automatically — you never have to pick which bill a payment applies to.",
    steps: [
      { icon: "fa-layer-group", title: "1. Create Class Groups & Streams", body: "e.g. Primary One (P1) as a group, with streams P1A / P1B inside it.", link: "/accounting/fees/structure" },
      { icon: "fa-tags", title: "2. Add Fee Items", body: "Tuition, boarding, transport — whatever you bill for.", link: "/accounting/fees/structure" },
      { icon: "fa-user-graduate", title: "3. Register & enroll students", body: "Assign each student to a stream for the current term.", link: "/accounting/fees" },
      { icon: "fa-people-group", title: "4. Bill a Class Group", body: "Pick items + amounts once — every enrolled student in every stream of that group gets billed for the term.", link: "/accounting/fees" },
      { icon: "fa-money-bill-wave", title: "5. Record Payments", body: "Pick the student, enter the amount — it auto-applies to their oldest bill, and any extra carries forward as credit.", link: "/accounting/fees" },
      { icon: "fa-chart-pie", title: "6. Fees Dashboard", body: "Expected vs collected revenue, drill from group down to stream.", link: "/accounting/fees/dashboard" },
      { icon: "fa-magnifying-glass", title: "7. Quick Search", body: "Find any student by admission number or name to see their full billing/payment history and balance.", link: "/accounting/fees" },
      { icon: "fa-mobile-screen", title: "8. SchoolPay (optional)", body: "Auto-match incoming mobile-money payments to students by their SchoolPay code.", link: "/accounting/schoolpay" },
      { icon: "fa-plug", title: "9. Connect your existing school system (optional)", body: "If you already run a student/academics system, generate an API key here and sync class groups, streams, enrollment and billing directly — no double data entry.", link: "/accounting/integration/keys" },
    ],
    tips: [
      "A student's balance is always \"brought forward\" — you don't reconcile per-term, the running total does it for you.",
      "Re-running \"Bill Class Group\" for the same group/term only bills newly-enrolled students — it skips anyone already billed.",
    ],
  },
  trading: {
    label: "Trading / Retail",
    intro: "Sales flow through a Products/Services catalogue with stock tracking; purchases post to Raw Materials or expense accounts as configured.",
    steps: [
      { icon: "fa-box", title: "1. Build your item catalogue", body: "Products/Services for what you sell, Purchase Items for what you buy in.", link: "/accounting/items/sales" },
      { icon: "fa-file-invoice", title: "2. Raise a Sales Invoice", body: "Pick items — price, tax rate and account auto-fill from the catalogue. Add a discount if needed.", link: "/accounting/invoices/new?type=outgoing" },
      { icon: "fa-truck", title: "3. Record a Purchase Invoice", body: "Stock items automatically increase quantity on hand and post to the right asset account.", link: "/accounting/invoices/new?type=incoming" },
      { icon: "fa-hourglass-half", title: "4. Track what's unpaid", body: "Aging reports for both customers and suppliers.", link: "/accounting/invoices/aging?type=outgoing" },
      { icon: "fa-chart-pie", title: "5. Reports", body: "Trial Balance, P&L and Balance Sheet reflect COGS automatically as stock moves.", link: "/accounting/reports/trial-balance" },
    ],
    tips: [
      "Approving a sales invoice for a stock item posts the COGS entry for you — no manual journal needed.",
      "Low-stock items are flagged from the item catalogue pages.",
    ],
  },
  manufacturing: {
    label: "Manufacturing",
    intro: "Same invoicing/stock engine as Trading, with a dedicated Raw Materials asset account for production inputs.",
    steps: [
      { icon: "fa-box", title: "1. Catalogue raw materials & finished goods", body: "Purchase Items for inputs, Products/Services for what you sell.", link: "/accounting/items/purchase" },
      { icon: "fa-truck", title: "2. Record material purchases", body: "Posts to Raw Materials (1070) so input costs are tracked separately from finished-goods stock.", link: "/accounting/invoices/new?type=incoming" },
      { icon: "fa-file-invoice", title: "3. Invoice finished goods", body: "Sales invoices post revenue and release COGS automatically.", link: "/accounting/invoices/new?type=outgoing" },
      { icon: "fa-diagram-project", title: "4. Track costs by project/line", body: "Tag entries to a project or cost centre to see production costs separately.", link: "/accounting/projects" },
      { icon: "fa-gauge-high", title: "5. Managerial Accounting", body: "Cost and margin views beyond the standard financial statements.", link: "/accounting/managerial" },
    ],
    tips: [
      "Raw Materials (1070) and finished-goods Inventory (1060) are separate accounts — check both when reviewing stock value.",
    ],
  },
  services: {
    label: "Services",
    intro: "Billing is invoice- and project-led rather than stock-led — track costs and revenue per client engagement.",
    steps: [
      { icon: "fa-diagram-project", title: "1. Set up Projects / Cost Centres", body: "One per client engagement, retainer, or job.", link: "/accounting/projects" },
      { icon: "fa-file-invoice", title: "2. Invoice your clients", body: "Line items don't need a stock catalogue entry — describe the service directly on the invoice.", link: "/accounting/invoices/new?type=outgoing" },
      { icon: "fa-receipt", title: "3. Log expenses against a project", body: "Use a Payment Voucher tagged to the project to see true engagement profitability.", link: "/accounting/vouchers/new?type=PV" },
      { icon: "fa-money-check-dollar", title: "4. Payroll (if you have staff)", body: "Run payroll and generate payslips.", link: "/accounting/payroll" },
      { icon: "fa-chart-pie", title: "5. Reports", body: "P&L and Managerial Accounting show margin per project.", link: "/accounting/managerial" },
    ],
    tips: [
      "Tag both the invoice and any related expense vouchers to the same project to get an accurate per-engagement margin.",
    ],
  },
  ngo: {
    label: "NGO / Nonprofit",
    intro: "Track funding by project/grant and compare spend against budget — the Chart of Accounts is seeded with donor-fund-style accounts.",
    steps: [
      { icon: "fa-diagram-project", title: "1. Create a Project per grant/program", body: "So every transaction can be tagged to the funding source it belongs to.", link: "/accounting/projects" },
      { icon: "fa-coins", title: "2. Set a Budget per project", body: "Then compare actual spend against it at any time.", link: "/accounting/budget/new" },
      { icon: "fa-receipt", title: "3. Record income & expenses", body: "Vouchers or journal entries, tagged to the relevant project.", link: "/accounting/vouchers" },
      { icon: "fa-scale-balanced", title: "4. Budget vs Actual", body: "The core report for donor accountability.", link: "/accounting/budget/vs-actual" },
      { icon: "fa-chart-pie", title: "5. Financial Reports", body: "Standard statements plus period-based reporting for donor reports.", link: "/accounting/period-reports" },
    ],
    tips: [
      "Consistently tagging every entry to a project is what makes Budget vs Actual and donor reporting meaningful — untagged entries won't show up per-grant.",
    ],
  },
  hospitality: {
    label: "Hospitality",
    intro: "Room and service revenue flow through the same invoicing engine as any other sale — set up a catalogue item per rate/service.",
    steps: [
      { icon: "fa-box", title: "1. Catalogue your services", body: "Rooms, packages, F&B — one item per billable rate.", link: "/accounting/items/sales" },
      { icon: "fa-file-invoice", title: "2. Invoice guests", body: "Add multiple line items (room + extras) to one invoice.", link: "/accounting/invoices/new?type=outgoing" },
      { icon: "fa-truck", title: "3. Record supplier purchases", body: "Stock/consumables purchases for kitchen, housekeeping, etc.", link: "/accounting/invoices/new?type=incoming" },
      { icon: "fa-landmark", title: "4. Reconcile daily takings", body: "Match card/mobile-money settlements against the bank statement.", link: "/accounting/bank/reconcile" },
      { icon: "fa-chart-pie", title: "5. Reports", body: "P&L by period to track occupancy-driven revenue trends.", link: "/accounting/period-reports" },
    ],
    tips: [
      "Use one invoice per guest stay/folio rather than one per night — easier to reconcile at checkout.",
    ],
  },
  healthcare: {
    label: "Healthcare",
    intro: "Patient billing uses the standard invoicing engine — patients are Contacts, services are catalogue items.",
    steps: [
      { icon: "fa-address-book", title: "1. Add patients as Contacts", body: "Or link to an insurer/payer as the billed party.", link: "/accounting/contacts" },
      { icon: "fa-box", title: "2. Catalogue billable services", body: "Consultations, procedures, medication dispensed.", link: "/accounting/items/sales" },
      { icon: "fa-file-invoice", title: "3. Invoice per visit", body: "One invoice per patient encounter, itemised.", link: "/accounting/invoices/new?type=outgoing" },
      { icon: "fa-hourglass-half", title: "4. Track outstanding balances", body: "Aging report for unpaid patient/insurer invoices.", link: "/accounting/invoices/aging?type=outgoing" },
      { icon: "fa-chart-pie", title: "5. Reports", body: "Standard financial statements plus period reporting.", link: "/accounting/period-reports" },
    ],
    tips: [
      "If you bill insurers separately from patients, add each insurer as its own Contact so aging/collections are tracked per payer.",
    ],
  },
  generic: {
    label: "General Business",
    intro: "The core accounting engine — Chart of Accounts, Invoices, Vouchers, Ledger and Reports — fits most straightforward businesses as-is.",
    steps: [
      { icon: "fa-file-invoice", title: "1. Invoice customers / bill suppliers", body: "", link: "/accounting/invoices" },
      { icon: "fa-receipt", title: "2. Record anything else with a Voucher", body: "Payments, receipts, journal-style entries.", link: "/accounting/vouchers" },
      { icon: "fa-coins", title: "3. Set a Budget", body: "Optional, but useful for tracking spend against plan.", link: "/accounting/budget" },
      { icon: "fa-chart-pie", title: "4. Run Reports", body: "Trial Balance, P&L, Balance Sheet, Cash Flow.", link: "/accounting/reports/trial-balance" },
    ],
    tips: [],
  },
};

const UserGuide = () => {
  const { industry } = useContext(AuthContext);
  const guide = INDUSTRY_CONTENT[industry] || INDUSTRY_CONTENT.generic;
  const [tab, setTab] = useState("industry"); // industry | core

  return (
    <AppContainer
      title="User Guide"
      previousPageTitle="Help"
      description={`A quick-start guide tailored to your industry (${guide.label}) plus the core accounting workflow every business uses.`}
    >
      <div className="alert alert-primary d-flex align-items-center gap-2 mb-3">
        <i className="fa-solid fa-graduation-cap"></i>
        <div>
          Showing guidance for <strong>{guide.label}</strong>. Your industry is set from your company profile —
          change it under <code>Setup → System Config</code> if it's wrong.
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === "industry" ? "active" : ""}`} onClick={() => setTab("industry")}>
            <i className="fa-solid fa-star me-1"></i>Your {guide.label} Workflow
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "core" ? "active" : ""}`} onClick={() => setTab("core")}>
            <i className="fa-solid fa-book me-1"></i>Core Accounting Basics
          </button>
        </li>
      </ul>

      {tab === "industry" && (
        <div className="card custom-card p-4">
          <p className="tx-14">{guide.intro}</p>
          <div className="row g-3">
            {guide.steps.map((s) => (
              <div className="col-lg-6" key={s.title}>
                <Link to={s.link} className="d-block p-3 h-100" style={{ border: "1px solid var(--border-color, #e5e7eb)", borderRadius: "10px", textDecoration: "none", color: "inherit" }}>
                  <div className="d-flex align-items-start gap-3">
                    <div style={{ width: "38px", height: "38px", borderRadius: "50%", background: "#4f46e520", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`fa-solid ${s.icon}`} style={{ color: "#4f46e5" }}></i>
                    </div>
                    <div>
                      <div className="fw-semibold tx-14">{s.title}</div>
                      {s.body && <div className="tx-12 tx-muted mt-1">{s.body}</div>}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          {guide.tips.length > 0 && (
            <div className="mt-4">
              <h6 className="fw-semibold tx-13"><i className="fa-solid fa-lightbulb me-1"></i>Tips</h6>
              <ul className="tx-13 tx-muted">
                {guide.tips.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === "core" && (
        <div className="card custom-card p-4">
          <h6 className="fw-semibold mb-2">First-time setup</h6>
          <div className="row g-3 mb-4">
            {CORE_STEPS.map((s) => (
              <div className="col-lg-6" key={s.title}>
                <Link to={s.link} className="d-block p-3 h-100" style={{ border: "1px solid var(--border-color, #e5e7eb)", borderRadius: "10px", textDecoration: "none", color: "inherit" }}>
                  <div className="d-flex align-items-start gap-3">
                    <div style={{ width: "38px", height: "38px", borderRadius: "50%", background: "#22c55e20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`fa-solid ${s.icon}`} style={{ color: "#22c55e" }}></i>
                    </div>
                    <div>
                      <div className="fw-semibold tx-14">{s.title}</div>
                      <div className="tx-12 tx-muted mt-1">{s.body}</div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          <h6 className="fw-semibold mb-2">Day to day</h6>
          <div className="row g-3">
            {CORE_DAILY.map((s) => (
              <div className="col-lg-6" key={s.title}>
                <Link to={s.link} className="d-block p-3 h-100" style={{ border: "1px solid var(--border-color, #e5e7eb)", borderRadius: "10px", textDecoration: "none", color: "inherit" }}>
                  <div className="d-flex align-items-start gap-3">
                    <div style={{ width: "38px", height: "38px", borderRadius: "50%", background: "#f59e0b20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`fa-solid ${s.icon}`} style={{ color: "#f59e0b" }}></i>
                    </div>
                    <div>
                      <div className="fw-semibold tx-14">{s.title}</div>
                      <div className="tx-12 tx-muted mt-1">{s.body}</div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppContainer>
  );
};

export default UserGuide;
